// ============================================================
// MyGene — PageToolbar
// Global action + filter bar used above every page table.
// Replaces per-page "Buttons" components (UserButtons, etc.).
//
// Usage:
//   <PageToolbar
//     primaryAction={{ label: "Create User", icon: <PlusOutlined />, onClick: handleCreate }}
//     filters={[
//       { type: "select", placeholder: "Filter by role", options: roleOptions,
//         value: selectedRole, onChange: setSelectedRole, width: 180 },
//       { type: "search", placeholder: "Search users…",
//         value: searchTerm, onChange: setSearchTerm, width: 250 },
//     ]}
//     onReset={handleReset}
//   />
// ============================================================

import React, { ReactNode } from "react";
import { Button, Input, Select, Space } from "antd";
import { PlusOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import SectionCard from "./SectionCard";

const { Search } = Input;

// ── Filter definitions ─────────────────────────────────────────

interface BaseFilter {
  /** Unique key for this filter */
  key?: string;
  width?: number;
}

interface SearchFilter extends BaseFilter {
  type: "search";
  placeholder?: string;
  value?: string;
  onChange: (value: string) => void;
  /** Forwarded to `data-testid` on the rendered input, for the e2e journey suite. */
  testId?: string;
}

interface SelectFilter extends BaseFilter {
  type: "select";
  placeholder?: string;
  options: { label: string; value: string }[];
  value?: string;
  onChange: (value: string) => void;
  allowClear?: boolean;
}

interface CustomFilter extends BaseFilter {
  type: "custom";
  render: ReactNode;
}

export type ToolbarFilter = SearchFilter | SelectFilter | CustomFilter;

// ── Primary action ─────────────────────────────────────────────

export interface ToolbarAction {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  type?: "primary" | "default" | "dashed";
  danger?: boolean;
  disabled?: boolean;
  /** Optional e2e test anchor — rendered as data-testid on the button DOM element. */
  testId?: string;
}

// ── Props ──────────────────────────────────────────────────────

interface PageToolbarProps {
  /** The main CTA (rendered left, primary style) */
  primaryAction?: ToolbarAction;
  /** Additional action buttons rendered alongside primaryAction */
  extraActions?: ToolbarAction[];
  /** Filter inputs rendered on the right */
  filters?: ToolbarFilter[];
  /** Renders a Reset button and calls this when clicked */
  onReset?: () => void;
}

// ── Component ──────────────────────────────────────────────────

const PageToolbar: React.FC<PageToolbarProps> = ({
  primaryAction,
  extraActions,
  filters,
  onReset,
}) => {
  return (
    <SectionCard>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
        }}
      >
        {/* Left: primary + extra actions */}
        <Space size={8} wrap>
          {primaryAction && (
            <Button
              type={primaryAction.type ?? "primary"}
              icon={primaryAction.icon ?? <PlusOutlined />}
              danger={primaryAction.danger}
              disabled={primaryAction.disabled}
              onClick={primaryAction.onClick}
              data-testid={primaryAction.testId}
            >
              {primaryAction.label}
            </Button>
          )}
          {extraActions?.map((a, i) => (
            <Button
              key={i}
              type={a.type ?? "default"}
              icon={a.icon}
              danger={a.danger}
              disabled={a.disabled}
              onClick={a.onClick}
              data-testid={a.testId}
            >
              {a.label}
            </Button>
          ))}
        </Space>

        {/* Right: filters + reset */}
        <Space size={8} wrap>
          {filters?.map((f, i) => {
            const key = f.key ?? i;
            if (f.type === "search") {
              return (
                <Search
                  key={key}
                  placeholder={f.placeholder ?? "Search…"}
                  value={f.value}
                  onChange={(e) => f.onChange(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                  data-testid={f.testId}
                  style={{ width: f.width ?? 240 }}
                />
              );
            }
            if (f.type === "select") {
              return (
                <Select
                  key={key}
                  placeholder={f.placeholder}
                  options={f.options}
                  value={f.value || undefined}
                  onChange={(v) => f.onChange(v ?? "")}
                  allowClear={f.allowClear !== false}
                  style={{ width: f.width ?? 180 }}
                />
              );
            }
            // custom
            return <React.Fragment key={key}>{f.render}</React.Fragment>;
          })}
          {onReset && (
            <Button icon={<ReloadOutlined />} onClick={onReset}>
              Reset
            </Button>
          )}
        </Space>
      </div>
    </SectionCard>
  );
};

export default PageToolbar;
